import React, { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Box,
    IconButton,
    Chip,
    Alert,
    CircularProgress,
    Button,
    Divider,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Badge
} from '@mui/material';
import { 
    ArrowBack, 
    List, 
    ExpandMore, 
    Schedule, 
    CheckCircle, 
    Error,
    Refresh,
    Assignment
} from '@mui/icons-material';
import api from '../axios/axiosInstance';

const ViewTickets = ({ onBack }) => {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [alert, setAlert] = useState({ show: false, type: '', message: '' });
    const [refreshing, setRefreshing] = useState(false);

    useEffect(() => {
        fetchTickets();
    }, []);

    const fetchTickets = async () => {
        try {
            setLoading(true);
            const response = await api.get('/auth/tickets/');
            setTickets(response.data);
        } catch (error) {
            showAlert('error', 'Failed to load tickets. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        await fetchTickets();
        setRefreshing(false);
        showAlert('success', 'Tickets refreshed!');
    };

    const showAlert = (type, message) => {
        setAlert({ show: true, type, message });
        setTimeout(() => setAlert({ show: false, type: '', message: '' }), 3000);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'open': return '#ed6c02';
            case 'in_progress': return '#1976d2';
            case 'resolved': return '#2e7d32';
            default: return '#616161';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'open': return <Schedule sx={{ fontSize: 16 }} />;
            case 'in_progress': return <Assignment sx={{ fontSize: 16 }} />;
            case 'resolved': return <CheckCircle sx={{ fontSize: 16 }} />;
            default: return <Error sx={{ fontSize: 16 }} />;
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'low': return '#4caf50';
            case 'medium': return '#ff9800';
            case 'high': return '#f44336';
            case 'urgent': return '#9c27b0';
            default: return '#616161';
        }
    };

    const getIssueTypeColor = (issueType) => {
        const colors = {
            'Billing/Subscription': '#d32f2f',
            'Students': '#1976d2',
            'Assessment': '#2e7d32',
            'Teacher Resources': '#ed6c02',
            'Subject': '#7b1fa2',
            'Resources': '#0288d1',
            'Other': '#616161'
        };
        return colors[issueType] || '#616161';
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusCounts = () => {
        const counts = tickets.reduce((acc, ticket) => {
            acc[ticket.status] = (acc[ticket.status] || 0) + 1;
            return acc;
        }, {});
        return counts;
    };

    const statusCounts = getStatusCounts();

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, maxWidth: '1000px', margin: '0 auto' }}>
            {/* Header */}
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton onClick={onBack} sx={{ mr: 2 }}>
                        <ArrowBack />
                    </IconButton>
                    <List sx={{ fontSize: 32, color: '#ed6c02', mr: 1 }} />
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        Your Support Tickets
                    </Typography>
                    <Badge 
                        badgeContent={tickets.length} 
                        color="primary" 
                        sx={{ ml: 2 }}
                    >
                        <Box />
                    </Badge>
                </Box>
                <Button
                    variant="outlined"
                    startIcon={refreshing ? <CircularProgress size={16} /> : <Refresh />}
                    onClick={handleRefresh}
                    disabled={refreshing}
                >
                    Refresh
                </Button>
            </Box>

            {/* Alert */}
            {alert.show && (
                <Alert severity={alert.type} sx={{ mb: 3 }}>
                    {alert.message}
                </Alert>
            )}

            {/* Status Summary */}
            {tickets.length > 0 && (
                <Card sx={{ mb: 3, borderRadius: '12px' }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                            Ticket Summary
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            {Object.entries(statusCounts).map(([status, count]) => (
                                <Chip
                                    key={status}
                                    icon={getStatusIcon(status)}
                                    label={`${status.replace('_', ' ').toUpperCase()}: ${count}`}
                                    sx={{ 
                                        bgcolor: getStatusColor(status),
                                        color: 'white',
                                        '& .MuiChip-icon': { color: 'white' }
                                    }}
                                />
                            ))}
                        </Box>
                    </CardContent>
                </Card>
            )}

            {/* Tickets List */}
            {tickets.length === 0 ? (
                <Card sx={{ borderRadius: '12px', textAlign: 'center' }}>
                    <CardContent sx={{ p: 4 }}>
                        <Assignment sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                        <Typography variant="h6" gutterBottom color="text.secondary">
                            No Support Tickets
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            You haven't created any support tickets yet.
                        </Typography>
                    </CardContent>
                </Card>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {tickets.map((ticket) => (
                        <Accordion 
                            key={ticket.id}
                            sx={{ 
                                borderRadius: '12px !important',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                '&:before': { display: 'none' }
                            }}
                        >
                            <AccordionSummary 
                                expandIcon={<ExpandMore />}
                                sx={{ 
                                    borderRadius: '12px',
                                    '&.Mui-expanded': {
                                        borderBottomLeftRadius: 0,
                                        borderBottomRightRadius: 0
                                    }
                                }}
                            >
                                <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 2 }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                            <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
                                                #{ticket.ticket_number}
                                            </Typography>
                                            <Chip
                                                size="small"
                                                icon={getStatusIcon(ticket.status)}
                                                label={ticket.status.replace('_', ' ').toUpperCase()}
                                                sx={{ 
                                                    bgcolor: getStatusColor(ticket.status),
                                                    color: 'white',
                                                    '& .MuiChip-icon': { color: 'white' },
                                                    mr: 1
                                                }}
                                            />
                                            <Chip
                                                size="small"
                                                label={ticket.issue_type}
                                                sx={{ 
                                                    bgcolor: getIssueTypeColor(ticket.issue_type),
                                                    color: 'white'
                                                }}
                                            />
                                        </Box>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                                            {ticket.title}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Created: {formatDate(ticket.created_at)}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                        <Chip
                                            size="small"
                                            label={`${ticket.priority} priority`}
                                            sx={{ 
                                                bgcolor: getPriorityColor(ticket.priority),
                                                color: 'white',
                                                mb: 1
                                            }}
                                        />
                                        {ticket.updated_at !== ticket.created_at && (
                                            <Typography variant="caption" color="text.secondary">
                                                Updated: {formatDate(ticket.updated_at)}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pt: 0 }}>
                                <Divider sx={{ mb: 2 }} />
                                <Typography variant="body1" sx={{ lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                                    {ticket.description}
                                </Typography>
                                
                                {/* Ticket Info */}
                                <Box sx={{ mt: 3, p: 2, bgcolor: '#f8f9fa', borderRadius: '8px' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        <strong>Ticket Information:</strong><br />
                                        Status: {ticket.status.replace('_', ' ').toUpperCase()}<br />
                                        Priority: {ticket.priority.toUpperCase()}<br />
                                        Issue Type: {ticket.issue_type}<br />
                                        Created: {formatDate(ticket.created_at)}<br />
                                        {ticket.updated_at !== ticket.created_at && `Last Updated: ${formatDate(ticket.updated_at)}`}
                                    </Typography>
                                </Box>

                                {/* Action needed message */}
                                {ticket.status === 'open' && (
                                    <Alert severity="info" sx={{ mt: 2 }}>
                                        Your ticket is in our queue. We'll update you once we start working on it.
                                    </Alert>
                                )}
                                {ticket.status === 'in_progress' && (
                                    <Alert severity="warning" sx={{ mt: 2 }}>
                                        Our support team is currently working on your ticket.
                                    </Alert>
                                )}
                                {ticket.status === 'resolved' && (
                                    <Alert severity="success" sx={{ mt: 2 }}>
                                        This ticket has been resolved. If you need further assistance, please create a new ticket.
                                    </Alert>
                                )}
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            )}

            {/* Footer */}
            <Box sx={{ mt: 4, textAlign: 'center', p: 2, bgcolor: '#f8f9fa', borderRadius: '8px' }}>
                <Typography variant="body2" color="text.secondary">
                    Need to update a ticket or have questions? Email us at{' '}
                    <Typography component="span" sx={{ color: '#1976d2', fontWeight: 500 }}>
                        support@gradely.ca
                    </Typography>
                    {' '}with your ticket number.
                </Typography>
            </Box>
        </Box>
    );
};

export default ViewTickets;